import { StorageVars } from 'config/constants/storage';

/**
 * Detects if a Supabase error is an authentication/JWT error
 * that should trigger a session cleanup instead of crashing the app.
 *
 * Known error patterns:
 * - PGRST301: JWT expired (PostgREST)
 * - PGRST302: JWT role claim missing
 * - 401 status code from Supabase REST API
 * - AuthSessionMissingError / AuthApiError from @supabase/supabase-js
 */
export const isAuthError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const err = error as Record<string, unknown>;

  // PostgREST JWT errors (PGRST301 = expired, PGRST302 = role missing)
  if (typeof err.code === 'string' && err.code.startsWith('PGRST3')) return true;

  // Supabase REST API 401 status
  if (err.status === 401) return true;

  // Supabase auth library errors
  if (typeof err.name === 'string' && err.name.includes('AuthSessionMissing')) return true;
  if (typeof err.name === 'string' && err.name === 'AuthApiError') return true;

  // Check message as fallback
  if (typeof err.message === 'string') {
    const msg = err.message.toLowerCase();
    if (msg.includes('jwt expired') || (msg.includes('jwt') && msg.includes('invalid'))) return true;
  }

  return false;
};

let isHandlingAuthError = false;

/**
 * Handles an auth error by cleaning up the session and reloading.
 * Uses a flag to prevent multiple simultaneous cleanups.
 */
export const handleAuthError = (): void => {
  if (isHandlingAuthError) return;
  isHandlingAuthError = true;

  // Clear all auth-related storage
  localStorage.removeItem(StorageVars.BDS_USER_ID);
  localStorage.removeItem(StorageVars.REACT_QUERY_CACHE);
  localStorage.removeItem(StorageVars.LOCAL_SUPABASE_AUTH_TOKEN);

  // Redirect to demo page — use window.location to force a full reload
  // which resets all React state cleanly
  window.location.href = '/demo?session_expired=true';
};

// Exported for testing
export const _resetAuthErrorFlag = (): void => {
  isHandlingAuthError = false;
};
